import * as React from 'react'
import { Progress as ProgressPrimitive } from '@base-ui/react/progress'

import { cn } from '@safe-global/views/utils/cn'

/**
 * Progress Component
 *
 * Displays a progress bar (determinate or indeterminate).
 *
 * @see https://ui.shadcn.com/docs/components/base/progress
 *
 * @example
 * ```tsx
 * <Progress value={33} />
 * // or with label:
 * <Progress value={56}>
 *   <ProgressLabel>Upload progress</ProgressLabel>
 *   <ProgressValue />
 * </Progress>
 * ```
 *
 * @remarks
 * Key Props:
 * - Progress (Root): `value` (0–100), `min`, `max`
 * - ProgressLabel: accessible label text
 * - ProgressValue: displays percentage
 */

function Progress({
  className,
  children,
  value,
  indicatorClassName,
  ...props
}: ProgressPrimitive.Root.Props & { indicatorClassName?: string }) {
  // The track is auto-rendered for the bare `<Progress value={n} />` API, but skipped
  // when the caller composes `<ProgressTrack>` themselves (custom height/indicator).
  const hasOwnTrack = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && child.type === ProgressTrack,
  )
  return (
    <ProgressPrimitive.Root
      value={value}
      data-slot="progress"
      className={cn('flex flex-wrap gap-3', className)}
      {...props}
    >
      {children}
      {!hasOwnTrack && (
        <ProgressTrack>
          <ProgressIndicator className={indicatorClassName} />
        </ProgressTrack>
      )}
    </ProgressPrimitive.Root>
  )
}

function ProgressTrack({ className, ...props }: ProgressPrimitive.Track.Props) {
  return (
    <ProgressPrimitive.Track
      className={cn('bg-muted h-1.5 rounded-full relative flex w-full items-center overflow-x-hidden', className)}
      data-slot="progress-track"
      {...props}
    />
  )
}

function ProgressIndicator({ className, ...props }: ProgressPrimitive.Indicator.Props) {
  return (
    <ProgressPrimitive.Indicator
      data-slot="progress-indicator"
      className={cn('bg-primary h-full transition-all', className)}
      {...props}
    />
  )
}

function ProgressLabel({ className, ...props }: ProgressPrimitive.Label.Props) {
  return (
    <ProgressPrimitive.Label className={cn('text-sm font-medium', className)} data-slot="progress-label" {...props} />
  )
}

function ProgressValue({ className, ...props }: ProgressPrimitive.Value.Props) {
  return (
    <ProgressPrimitive.Value
      className={cn('text-muted-foreground ml-auto text-sm tabular-nums', className)}
      data-slot="progress-value"
      {...props}
    />
  )
}

export { Progress, ProgressTrack, ProgressIndicator, ProgressLabel, ProgressValue }
